import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Map, Source, Layer, Popup } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Button,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';

import { LocationsTabs } from './LocationsTabs';
import axiosInstance from 'utils/axiosUtil';

const mapboxToken =
  'pk.eyJ1IjoiZG9jdGl2aXR5IiwiYSI6ImNsMmo2OWx6bTBtdmUza3J6eDQ2Z3pvdG0ifQ.o9j6xJxOa2WKQLYdqQaJRQ';

// Fallback center when there is no requested location and no saved last-position:
// Doctivity's office in Bloomsburg, PA.
const BLOOMSBURG_OFFICE = { lat: 41.0011, lng: -76.4257 };
const DEFAULT_ZOOM = 12;
const LAST_POSITION_KEY = 'locationsMap.lastPosition';
// How long to wait after the map stops moving before hitting the DB (ms).
const FETCH_DEBOUNCE_MS = 400;
// Overall ceiling on how many locations we'll load for one view...
const MAX_LOCATIONS = 2000;
// ...paged in this many at a time so a zoomed-out area fills in progressively instead of stalling.
const BATCH_SIZE = 100;
// Below this zoom the area covers too many locations to be useful/fast — we show nothing and ask the
// user to zoom in. (Higher zoom = more zoomed in; zooming out lowers the number past this floor.)
const MIN_ZOOM = 10;

// Per-browser "last place you were" persistence. Wrapped in try/catch: localStorage can throw or
// be unavailable (private windows, blocked site data), and we must render fine without it.
const readLastPosition = () => {
  try {
    const raw = window.localStorage.getItem(LAST_POSITION_KEY);
    if (raw) {
      const pos = JSON.parse(raw);
      if (typeof pos?.lat === 'number' && typeof pos?.lng === 'number') {
        return pos;
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const writeLastPosition = (pos) => {
  try {
    window.localStorage.setItem(LAST_POSITION_KEY, JSON.stringify(pos));
  } catch (e) {
    // ignore
  }
};

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
    height: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    overflow: 'visible',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 300,
  },
  searchField: {
    flex: 1,
    maxWidth: 500,
  },
  mapContainer: {
    position: 'relative',
    flex: 1,
    minHeight: 400,
    borderRadius: 4,
    overflow: 'visible',
    '& .mapboxgl-popup': {
      zIndex: '1000 !important',
    },
    '& .mapboxgl-popup-content': {
      maxHeight: '400px !important',
      overflowY: 'auto !important',
      padding: '12px 14px !important',
    },
  },
  zoomMessage: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(255, 255, 255, 0.92)',
    padding: theme.spacing(2, 3),
    borderRadius: 8,
    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.text.primary,
    zIndex: 5,
    pointerEvents: 'none',
    textAlign: 'center',
  },
  popupName: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 2,
  },
  popupAddress: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  popupLinks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: 8,
    gap: 2,
  },
  popupLink: {
    textTransform: 'none',
    padding: '2px 0',
    minWidth: 0,
    justifyContent: 'flex-start',
    textAlign: 'left',
    // Let a long "Organization: ..." wrap flush-left instead of hanging-indenting under the label.
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
});

// Compute a radius (miles) that encloses the visible viewport corners, so pins fill the screen.
// This is the fast, screen-driven radius the user experiences as "the viewable area" — we no longer
// draw a circle for it.
const radiusFromMap = (map) => {
  const center = map.getCenter();
  const bounds = map.getBounds();
  const ne = bounds.getNorthEast();
  const latMiles = Math.abs(ne.lat - center.lat) * 69; // center -> top edge
  const lngMiles = Math.abs(ne.lng - center.lng) * 69 * Math.cos((center.lat * Math.PI) / 180);
  // Corner distance (half-diagonal) covers the whole viewport, not just the mid-edges.
  return Math.max(1, Math.round(Math.sqrt(latMiles * latMiles + lngMiles * lngMiles)));
};

function LocationsMapPage({ classes }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.user);

  const mapRef = useRef(null);
  const fetchTimerRef = useRef(null);
  // Monotonic request id — only the latest area load is applied (drops stale/superseded pans).
  const fetchSeqRef = useRef(0);
  // Client-side counts cache keyed by location id. undefined = never fetched, null = in-flight,
  // object = loaded. Counts are only needed for the click popup, so they're fetched on demand.
  const countsCacheRef = useRef({});

  const [searchAddress, setSearchAddress] = useState(() => searchParams.get('address') || '');
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setCountsVersion] = useState(0); // bump to re-render when a popup's counts arrive
  const [selectedInfo, setSelectedInfo] = useState(null); // clicked-pin popup
  const [cursor, setCursor] = useState('grab');
  const [belowMinZoom, setBelowMinZoom] = useState(false);

  // Location the map was deep-linked FROM (via ?locationId=) — excluded from results so we don't
  // re-plot the record the user is already looking at.
  const [centerLocation, setCenterLocation] = useState(() => {
    const locationId = searchParams.get('locationId');
    return locationId ? { id: parseInt(locationId, 10) } : null;
  });

  // Initial camera, resolved ONCE: requested location (URL) -> saved last-position -> Bloomsburg office.
  const [initialView] = useState(() => {
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { latitude: lat, longitude: lng, zoom: DEFAULT_ZOOM };
    }
    const last = readLastPosition();
    if (last) {
      return { latitude: last.lat, longitude: last.lng, zoom: last.zoom || DEFAULT_ZOOM };
    }
    return { latitude: BLOOMSBURG_OFFICE.lat, longitude: BLOOMSBURG_OFFICE.lng, zoom: DEFAULT_ZOOM };
  });

  // Counts for one location, fetched lazily when its pin is clicked (they only show in the popup).
  const fetchCountsForId = useCallback((id) => {
    if (countsCacheRef.current[id] !== undefined) return; // cached or in-flight
    countsCacheRef.current[id] = null;
    axiosInstance
      .get(`Locations/location-counts?ids=${id}`)
      .then((response) => {
        const data = response.data || {};
        countsCacheRef.current[id] = data[id] || {
          provider_count: 0,
          contact_count: 0,
          organization_id: null,
          organization_name: null,
        };
        setCountsVersion((v) => v + 1);
      })
      .catch((error) => {
        console.error('Error fetching location counts:', error);
        if (countsCacheRef.current[id] === null) delete countsCacheRef.current[id];
      });
  }, []);

  // Load the current viewport's locations, paged BATCH_SIZE at a time up to MAX_LOCATIONS. Each page
  // is appended so pins fill in progressively; a newer pan (fetchSeqRef bump) abandons this loop.
  const loadArea = useCallback(
    async (lat, lng, radiusMiles) => {
      const seq = fetchSeqRef.current + 1;
      fetchSeqRef.current = seq;
      setLoading(true);
      const excludeParam = centerLocation?.id ? `&excludeLocationId=${centerLocation.id}` : '';
      const all = [];
      try {
        for (let offset = 0; offset < MAX_LOCATIONS; offset += BATCH_SIZE) {
          const url = `Locations/nearby-locations?lat=${lat}&lng=${lng}&radius=${radiusMiles}&limit=${BATCH_SIZE}&offset=${offset}${excludeParam}`;
          // Sequential on purpose: each page appends and paints before the next is requested.
          // eslint-disable-next-line no-await-in-loop
          const response = await axiosInstance.get(url);
          if (seq !== fetchSeqRef.current) return; // superseded by a newer pan
          const batch = response.data || [];
          all.push(...batch);
          setNearbyLocations(all.slice());
          if (batch.length < BATCH_SIZE) break; // last page
        }
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;
        console.error('Error fetching nearby locations:', error);
        setNearbyLocations([]);
      } finally {
        if (seq === fetchSeqRef.current) setLoading(false);
      }
    },
    [centerLocation]
  );

  // Fires on initial load and after every pan/zoom. Saves position, gates on zoom, debounces the load.
  const handleMapMoveEnd = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const center = map.getCenter();
    const zoom = map.getZoom();

    writeLastPosition({ lat: center.lat, lng: center.lng, zoom });
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);

    if (zoom < MIN_ZOOM) {
      // Too far out: cancel any in-flight load, clear pins, and prompt to zoom in.
      fetchSeqRef.current += 1;
      setNearbyLocations([]);
      setBelowMinZoom(true);
      setLoading(false);
      return;
    }

    setBelowMinZoom(false);
    const radiusMiles = radiusFromMap(map);
    fetchTimerRef.current = setTimeout(() => {
      loadArea(center.lat, center.lng, radiusMiles);
    }, FETCH_DEBOUNCE_MS);
  }, [loadArea]);

  // React to a NEW deep-link arriving while the page is already mounted (initialView only covers
  // mount). Skip the first run so we don't double-handle the initial params.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    const locationId = searchParams.get('locationId');
    setCenterLocation(locationId ? { id: parseInt(locationId, 10) } : null);
    if (!Number.isNaN(lat) && !Number.isNaN(lng) && mapRef.current) {
      mapRef.current.flyTo({ center: [lng, lat], zoom: DEFAULT_ZOOM, duration: 1000 });
    }
  }, [searchParams]);

  useEffect(() => () => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchAddress.trim()) return;
    setLoading(true);
    try {
      const encodedAddress = encodeURIComponent(searchAddress);
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}&limit=1`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        setCenterLocation(null);
        if (mapRef.current) {
          // flyTo triggers onMoveEnd -> load, so we don't load here directly.
          mapRef.current.flyTo({ center: [lng, lat], zoom: DEFAULT_ZOOM, duration: 1000 });
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      setLoading(false);
    }
  }, [searchAddress]);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleMapClick = useCallback(
    (event) => {
      if (!mapRef.current) return;
      const features = mapRef.current.queryRenderedFeatures(event.point, {
        layers: ['clusters', 'unclustered-point'],
      });
      if (!features.length) {
        setSelectedInfo(null);
        return;
      }
      const feature = features[0];
      if (feature.layer.id === 'clusters') {
        const clusterId = feature.properties.cluster_id;
        const source = mapRef.current.getSource('locations');
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          mapRef.current.easeTo({ center: feature.geometry.coordinates, zoom });
        });
      } else {
        setSelectedInfo({
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          properties: feature.properties,
        });
        fetchCountsForId(feature.properties.id);
      }
    },
    [fetchCountsForId]
  );

  const formatAddress = (location) => {
    const parts = [location.address1, location.city, location.state, location.postal_code].filter(
      Boolean
    );
    return parts.join(', ');
  };

  const geojson = {
    type: 'FeatureCollection',
    features: nearbyLocations
      .filter(
        (loc) => !Number.isNaN(parseFloat(loc.latitude)) && !Number.isNaN(parseFloat(loc.longitude))
      )
      .map((location) => ({
        type: 'Feature',
        properties: {
          id: location.id,
          name: location.name || 'Location',
          address: formatAddress(location),
          organization_id: location.organization_id || 0,
        },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(location.longitude), parseFloat(location.latitude)],
        },
      })),
  };

  // Admin gate.
  if (!user?.is_admin) {
    return (
      <>
        <LocationsTabs tab='locations_map' />
        <div className={classes.container}>
          <Card>
            <CardContent>
              <Typography>Access denied. Admin privileges required.</Typography>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const selectedCounts = selectedInfo
    ? countsCacheRef.current[selectedInfo.properties.id] || {}
    : {};
  const selectedOrgId =
    selectedInfo && parseInt(selectedInfo.properties.organization_id, 10)
      ? parseInt(selectedInfo.properties.organization_id, 10)
      : null;

  return (
    <>
      <LocationsTabs tab='locations_map' />
      <div className={classes.container}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <Box className={classes.controls}>
              <div className={classes.searchContainer}>
                <TextField
                  className={classes.searchField}
                  placeholder='Search address...'
                  variant='outlined'
                  size='small'
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton onClick={handleSearch} edge='end' size='small'>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              {loading && (
                <div className={classes.loading}>
                  <CircularProgress size={16} />
                  <span>Loading...</span>
                </div>
              )}
            </Box>

            <div className={classes.mapContainer}>
              <Map
                mapLib={import('mapbox-gl')}
                ref={mapRef}
                initialViewState={initialView}
                style={{ width: '100%', height: '100%' }}
                mapStyle='mapbox://styles/mapbox/light-v10'
                mapboxAccessToken={mapboxToken}
                attributionControl={false}
                cursor={cursor}
                onLoad={handleMapMoveEnd}
                onMoveEnd={handleMapMoveEnd}
                onClick={handleMapClick}
                onMouseEnter={() => setCursor('pointer')}
                onMouseLeave={() => setCursor('grab')}
                interactiveLayerIds={['clusters', 'unclustered-point']}
              >
                {/* Clustered location markers */}
                {geojson.features.length > 0 && (
                  <Source
                    id='locations'
                    type='geojson'
                    data={geojson}
                    cluster
                    clusterMaxZoom={14}
                    clusterRadius={50}
                  >
                    <Layer
                      id='clusters'
                      type='circle'
                      filter={['has', 'point_count']}
                      paint={{
                        'circle-color': '#1976d2',
                        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 25, 30],
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#fff',
                      }}
                    />
                    <Layer
                      id='cluster-count'
                      type='symbol'
                      filter={['has', 'point_count']}
                      layout={{
                        'text-field': '{point_count_abbreviated}',
                        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                        'text-size': 12,
                      }}
                      paint={{ 'text-color': '#ffffff' }}
                    />
                    <Layer
                      id='unclustered-point'
                      type='circle'
                      filter={['!', ['has', 'point_count']]}
                      paint={{
                        'circle-color': '#1976d2',
                        'circle-radius': 8,
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#fff',
                      }}
                    />
                  </Source>
                )}

                {/* Clicked-pin popup with drill-in links */}
                {selectedInfo && (
                  <Popup
                    longitude={selectedInfo.longitude}
                    latitude={selectedInfo.latitude}
                    anchor='bottom'
                    closeButton
                    closeOnClick={false}
                    offset={15}
                    maxWidth='560px'
                    onClose={() => setSelectedInfo(null)}
                  >
                    <div>
                      <div className={classes.popupName}>{selectedInfo.properties.name}</div>
                      <div className={classes.popupAddress}>{selectedInfo.properties.address}</div>
                      <Divider sx={{ my: 1 }} />
                      <div className={classes.popupLinks}>
                        <Button
                          className={classes.popupLink}
                          size='small'
                          onClick={() => navigate(`/locations/${selectedInfo.properties.id}`)}
                        >
                          Location details
                        </Button>
                        <Divider sx={{ my: 0.5, alignSelf: 'stretch' }} />
                        {selectedOrgId && (
                          <Button
                            className={classes.popupLink}
                            size='small'
                            onClick={() => navigate(`/organizations/${selectedOrgId}`)}
                          >
                            {selectedCounts.organization_name
                              ? `Organization: ${selectedCounts.organization_name}`
                              : 'Organization'}
                          </Button>
                        )}
                        <Button
                          className={classes.popupLink}
                          size='small'
                          onClick={() =>
                            navigate(`/locations/${selectedInfo.properties.id}/providers`)}
                        >
                          Providers
                          {selectedCounts.provider_count != null
                            ? ` (${selectedCounts.provider_count})`
                            : ''}
                        </Button>
                        <Button
                          className={classes.popupLink}
                          size='small'
                          onClick={() =>
                            navigate(`/locations/${selectedInfo.properties.id}/contacts`)}
                        >
                          Contacts
                          {selectedCounts.contact_count != null
                            ? ` (${selectedCounts.contact_count})`
                            : ''}
                        </Button>
                      </div>
                    </div>
                  </Popup>
                )}
              </Map>

              {belowMinZoom && (
                <div className={classes.zoomMessage}>Zoom in to see locations</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

LocationsMapPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styled = withStyles(styles)(LocationsMapPage);
export { styled as LocationsMapPage };
